import { useEffect, useState } from "react";
import { Images, Download, Share2, Loader2, Globe, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import api, { duckTitle, sharePageUrl } from "../lib/api";

const FORMATS = [
  { id: "portrait", label: "Portrait · 1080×1350" },
  { id: "landscape", label: "Landscape · 1200×630" },
];

const fileName = (creation, fmt) => `Duckify_${(duckTitle(creation) || "duck").replace(/[^\w-]+/g, "_")}_before-after_${fmt}.png`;

// Owner-only before/after card (photo → painted duck): preview, download, native share, and the public opt-in switch.
export const BeforeAfterCard = ({ creation, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [fmt, setFmt] = useState("portrait");
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shared, setShared] = useState(!!creation.share_before_after);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setBlob(null);
    api
      .get(`/creations/${creation.id}/before-after.png`, { params: { fmt }, responseType: "blob" })
      .then((r) => {
        if (!cancelled) setBlob(r.data);
      })
      .catch(() => !cancelled && toast.error("Couldn't build the card — please try again."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, fmt, creation.id]);

  useEffect(() => {
    if (!blob) return setUrl(null);
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  const download = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName(creation, fmt);
    a.click();
  };

  const share = async () => {
    const file = new File([blob], fileName(creation, fmt), { type: "image/png" });
    const text = `${duckTitle(creation) || "My duck"} — made with Duckify3D`;
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: text, text, url: sharePageUrl(creation.id) });
        return;
      }
      await navigator.clipboard.writeText(sharePageUrl(creation.id));
      toast.success("Sharing files isn't supported here — duck link copied instead. Download the PNG to post it.");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share — download the PNG instead.");
    }
  };

  const toggleShared = async (next) => {
    setShared(next);
    setBusy(true);
    try {
      const { data } = await api.post(`/creations/${creation.id}/visibility`, { share_before_after: next });
      onUpdate?.(data);
      toast.success(next ? "Before/after card is now on your duck's public page." : "Before/after card is private again.");
    } catch {
      setShared(!next);
      toast.error("Couldn't update — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button data-testid="before-after-btn" variant="outline" onClick={() => setOpen(true)} className="w-full h-12 rounded-full font-bold gap-2">
        <Images className="h-5 w-5" /> Before/after card
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="before-after-dialog" className="max-w-2xl rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Before / after card</DialogTitle>
            <DialogDescription>Your photo next to the printable duck — ready for Instagram, stories or a chat.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2" data-testid="before-after-format">
            {FORMATS.map((f) => (
              <button
                key={f.id}
                type="button"
                data-testid={`before-after-fmt-${f.id}`}
                onClick={() => setFmt(f.id)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${fmt === f.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className={`relative grid place-items-center rounded-2xl bg-muted/40 overflow-hidden mx-auto ${fmt === "portrait" ? "aspect-[4/5] h-[min(60vh,560px)]" : "aspect-[1200/630] w-full"}`}>
            {url ? (
              <img data-testid="before-after-preview" src={url} alt="before and after card" className="h-full w-full object-contain" />
            ) : (
              <Loader2 className="h-7 w-7 animate-spin text-accent" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="before-after-download-btn" onClick={download} disabled={!url || loading} className="rounded-full font-bold gap-2 h-11">
              <Download className="h-4 w-4" /> Download PNG
            </Button>
            <Button data-testid="before-after-share-btn" variant="secondary" onClick={share} disabled={!blob || loading} className="rounded-full font-bold gap-2 h-11">
              <Share2 className="h-4 w-4" /> Share
            </Button>
          </div>
          <div data-testid="before-after-public-toggle" className="flex items-center gap-3 rounded-2xl border border-border bg-card/60 px-4 py-3">
            {shared ? <Globe className="h-4 w-4 text-accent shrink-0" /> : <Lock className="h-4 w-4 text-muted-foreground shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Show on the duck's public page</p>
              <p className="text-xs text-muted-foreground">Off by default — your photo stays private. When on, the card (with your photo) appears on the share page and in link previews.</p>
            </div>
            <Switch data-testid="before-after-public-switch" checked={shared} disabled={busy} onCheckedChange={toggleShared} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
